import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  FileText,
  DollarSign,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  User,
  Briefcase
} from 'lucide-react';
import { supabase } from '../services/supabaseService';
import authService from '../services/authService';
import { usePermissions } from '../hooks/usePermissions';
import { formatDate, formatTime } from '../utils/textUtils';

interface CollaboratorStats {
  totalProjects: number;
  activeProjects: number;
  totalHours: number;
  thisMonthHours: number;
  pendingInvoices: number;
  totalInvoiceAmount: number;
  lastPaymentDate: string | null;
  hourlyRate: number;
  currency: string;
}

interface UpcomingMeeting {
  id: string;
  title: string;
  scheduledDate: string;
  scheduledTime: string;
  duration: number;
  meetingType: string;
}

interface RecentTimesheet {
  id: string;
  projectName: string;
  hours: number;
  description: string;
  date: string;
}

interface AssignedProject {
  id: string;
  name: string;
  clientName: string;
  status: string;
}

const CollaboratorDashboard: React.FC = () => {
  const currentUser = authService.getCurrentSession()?.user;
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<CollaboratorStats>({
    totalProjects: 0,
    activeProjects: 0,
    totalHours: 0,
    thisMonthHours: 0,
    pendingInvoices: 0,
    totalInvoiceAmount: 0,
    lastPaymentDate: null,
    hourlyRate: 0,
    currency: 'EUR'
  });
  const [upcomingMeetings, setUpcomingMeetings] = useState<UpcomingMeeting[]>([]);
  const [recentTimesheets, setRecentTimesheets] = useState<RecentTimesheet[]>([]);
  const [assignedProjects, setAssignedProjects] = useState<AssignedProject[]>([]);

  useEffect(() => {
    if (currentUser) {
      loadDashboardData();
    }
  }, [currentUser]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get user_id for current user
      const userId = currentUser!.id;
      console.log('🔍 DEBUG - user_id actual:', userId);

      const { data: userData } = await supabase
        .from('users')
        .select('role, hourly_rate, currency')
        .eq('id', userId)
        .single();

      const collaboratorData = userData ? { hourlyRate: userData.hourly_rate, currency: userData.currency } : null;

      // Get projects assigned to this user through project_assignments
      const { data: projectAssignments } = await supabase
        .from('project_assignments')
        .select(`
          project_id,
          user_id,
          projects (id, name, client_name, status)
        `)
        .eq('user_id', userId);

      const finalProjectAssignments = projectAssignments;

      // Extract projects from assignments
      const projects = (finalProjectAssignments || [])
        .map(pa => pa.projects)
        .filter(p => p !== null);

      console.log('🔍 DEBUG - projects extraídos:', projects);

      // Set assigned projects with full details
      const assignedProjectsList = projects.map((p: any) => ({
        id: p.id,
        name: p.name,
        clientName: p.client_name,
        status: p.status
      }));

      console.log('🔍 DEBUG - assignedProjectsList final:', assignedProjectsList);
      setAssignedProjects(assignedProjectsList);

      // Fetch user-specific data (timesheets, invoices, payments)
      const timesheetsResult = await supabase
        .from('timesheets')
        .select('hours, date')
        .eq('user_id', userId);
      const timesheets = timesheetsResult.data;

      const invoicesResult = await supabase
        .from('user_invoices')
        .select('amount, status')
        .eq('user_id', userId);
      const invoices = invoicesResult.data;

      const paymentsResult = await supabase
        .from('payments')
        .select('payment_date')
        .eq('user_id', userId)
        .order('payment_date', { ascending: false })
        .limit(1);
      const payments = paymentsResult.data;

      const recentTimesheetsResult = await supabase
        .from('timesheets')
        .select(`
          id,
          hours,
          description,
          date,
          projects:project_id (name)
        `)
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .limit(5);
      const recentTimesheetsData = recentTimesheetsResult.data;

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const thisMonthTimesheets = timesheets?.filter(
        t => new Date(t.date) >= startOfMonth
      ) || [];

      const pendingInvoices = invoices?.filter(
        i => i.status === 'received' || i.status === 'pending'
      ) || [];

      const { data: meetings, error: meetingsError } = await supabase
        .from('meetings')
        .select('id, title, datetime, duration, status')
        .eq('owner', currentUser!.id)
        .gte('datetime', new Date().toISOString())
        .order('datetime', { ascending: true })
        .limit(5);

      if (meetingsError) {
        console.error('Error fetching meetings:', meetingsError);
      }

      setStats({
        totalProjects: projects?.length || 0,
        activeProjects: projects?.filter(p => p.status === 'active').length || 0,
        totalHours: timesheets?.reduce((sum, t) => sum + t.hours, 0) || 0,
        thisMonthHours: thisMonthTimesheets.reduce((sum, t) => sum + t.hours, 0),
        pendingInvoices: pendingInvoices.length,
        totalInvoiceAmount: pendingInvoices.reduce((sum, i) => sum + i.amount, 0),
        lastPaymentDate: payments?.[0]?.payment_date || null,
        hourlyRate: collaboratorData?.hourlyRate || 0,
        currency: collaboratorData?.currency || 'EUR'
      });

      setUpcomingMeetings(
        meetings?.map(m => ({
          id: m.id,
          title: m.title,
          scheduledDate: m.scheduled_date,
          scheduledTime: m.scheduled_time,
          duration: m.duration,
          meetingType: m.meeting_type
        })) || []
      );

      setRecentTimesheets(
        recentTimesheetsData?.map(t => ({
          id: t.id,
          projectName: (t.projects as any)?.name || 'Proyecto',
          hours: t.hours,
          description: t.description || '',
          date: t.date
        })) || []
      );

    } catch (error) {
      console.error('Error loading collaborator dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Cargando tu panel...</p>
        </div>
      </div>
    );
  }

  const estimatedMonthlyEarnings = stats.thisMonthHours * stats.hourlyRate;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Bienvenido, {currentUser?.name}
          </h1>
          <p className="text-gray-600 mt-2">
            Este es tu panel de colaborador
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Proyectos Activos</p>
              <p className="text-3xl font-bold mt-2">{stats.activeProjects}</p>
              <p className="text-blue-100 text-xs mt-1">de {stats.totalProjects} totales</p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Briefcase className="w-8 h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Horas Este Mes</p>
              <p className="text-3xl font-bold mt-2">{stats.thisMonthHours}</p>
              <p className="text-green-100 text-xs mt-1">
                ≈ {estimatedMonthlyEarnings.toFixed(0)} {stats.currency}
              </p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Clock className="w-8 h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Facturas Pendientes</p>
              <p className="text-3xl font-bold mt-2">{stats.pendingInvoices}</p>
              <p className="text-orange-100 text-xs mt-1">
                {stats.totalInvoiceAmount.toFixed(0)} {stats.currency}
              </p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <FileText className="w-8 h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Total Horas</p>
              <p className="text-3xl font-bold mt-2">{stats.totalHours}</p>
              <p className="text-purple-100 text-xs mt-1">
                {stats.hourlyRate} {stats.currency}/hora
              </p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <TrendingUp className="w-8 h-8" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Próximas Reuniones</h2>
            </div>
          </div>
          <div className="p-6 min-h-[300px]">
            {upcomingMeetings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No tienes reuniones próximas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingMeetings.map(meeting => (
                  <div
                    key={meeting.id}
                    className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex-shrink-0 p-2 bg-blue-100 rounded">
                      <Calendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {meeting.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(meeting.scheduledDate)} • {formatTime(meeting.scheduledTime)} • {meeting.duration} min
                      </p>
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        {meeting.meetingType}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Últimos Partes de Horas</h2>
            </div>
          </div>
          <div className="p-6 min-h-[300px]">
            {recentTimesheets.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No hay partes de horas registrados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentTimesheets.map(timesheet => (
                  <div
                    key={timesheet.id}
                    className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors"
                  >
                    <div className="flex-shrink-0 p-2 bg-green-100 rounded">
                      <Clock className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {timesheet.projectName}
                        </p>
                        <span className="text-sm font-semibold text-green-600">
                          {timesheet.hours}h
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(timesheet.date)}
                      </p>
                      {timesheet.description && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {timesheet.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Proyectos Asignados Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Mis Proyectos Asignados</h2>
            </div>
            <span className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-full">
              {assignedProjects.length} proyecto{assignedProjects.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="p-6">
          {assignedProjects.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No tienes proyectos asignados</p>
              <p className="text-gray-400 text-sm mt-2">
                Contacta con el administrador para ser asignado a proyectos
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assignedProjects.map(project => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => {
                    // El dashboard vive en su propia tab. Primero
                    // saltamos al módulo de Colaboradores (tab 'pagos')
                    // y con un pequeño defer despachamos el evento del
                    // proyecto, ya con CollaboratorsModule montado y su
                    // listener registrado.
                    window.dispatchEvent(new CustomEvent('navigate-to-tab', {
                      detail: { tab: 'pagos' }
                    }));
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('navigate-to-project', {
                        detail: { projectId: project.id }
                      }));
                    }, 120);
                  }}
                  className="text-left border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
                        {project.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {project.clientName}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ml-2 ${
                        project.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : project.status === 'completed'
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {project.status === 'active' ? 'Activo' : project.status === 'completed' ? 'Completado' : 'Pausado'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-blue-600 font-medium">
                    <Briefcase className="w-3 h-3" />
                    <span>Abrir proyecto →</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {stats.lastPaymentDate && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mt-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Último Pago Recibido</p>
              <p className="text-xs text-gray-600 mt-0.5">
                {formatDate(stats.lastPaymentDate)}
              </p>
            </div>
          </div>
        </div>
      )}

      {stats.pendingInvoices > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                Tienes {stats.pendingInvoices} factura{stats.pendingInvoices > 1 ? 's' : ''} pendiente{stats.pendingInvoices > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                Total: {stats.totalInvoiceAmount.toFixed(2)} {stats.currency}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaboratorDashboard;
